// Comprehensive California family law data based on Custody X Change and official California sources
const californiaData = {
  'california': {
    name: 'California',
    overview: {
      keyPoints: [
        'California follows the "best interests of the child" standard with specific factors outlined in Family Code Section 3011',
        'California uses "time-sharing" terminology instead of traditional visitation language',
        'Shared parental responsibility is presumed unless proven detrimental to the child',
        'Parenting plans are mandatory in all cases involving minor children',
        'California has a complex child support formula based on both parents\' income and time spent with children',
        'The state requires mandatory mediation for all contested custody cases',
        'California has specific relocation restrictions requiring court approval for moves over 50 miles',
        'Domestic violence is taken very seriously with strong protective measures'
      ],
      uniqueFeatures: [
        'California uses "time-sharing" instead of visitation, emphasizing both parents\' ongoing roles',
        'The state has a 50-mile relocation threshold requiring court approval',
        'California allows for simplified dissolution for uncontested divorces',
        'Strong emphasis on mediation and collaborative approaches to resolve disputes',
        'California has specialized family drug courts in many counties',
        'The state recognizes domestic partnerships with full parental rights',
        'California has extensive self-help resources and family law facilitators',
        'The state allows for minor\'s counsel to be appointed in contested cases'
      ]
    },
    legalRequirements: {
      residency: 'Either spouse must be a California resident for at least 6 months and a resident of the filing county for at least 3 months before filing',
      filing: 'File in the county where either spouse resides or where the family lived when they separated',
      paternity: 'Unmarried fathers can establish paternity through voluntary declaration, genetic testing, court order, or presumption',
      documentation: 'Required documents include petition, summons, financial disclosures, and parenting plan in cases involving children',
      uniqueRules: [
        'California requires mandatory disclosure of all assets and debts in family law cases',
        'Mediation is required before contested custody hearings in most counties',
        'Guardian ad litem or minor\'s counsel appointment is common in contested custody cases',
        'Parent education programs are required in many counties for divorcing parents',
        'California allows for emergency orders to address immediate safety concerns'
      ]
    },
    custodyTypes: {
      physicalCustody: 'California uses "physical custody" to determine where the child primarily resides and who provides day-to-day care',
      legalCustody: 'Legal custody involves decision-making authority for major life decisions including education, healthcare, and religious upbringing',
      jointCustody: 'Joint custody (both legal and physical) is encouraged when it serves the child\'s best interests',
      visitation: 'Called "time-sharing" or "parenting time" - includes detailed schedules for when the child is with each parent',
      specialProvisions: [
        'Supervised visitation may be ordered when child safety is a concern',
        'California has detailed statutory time-sharing schedules for different age groups',
        'Extended summer time-sharing is standard to accommodate school schedules',
        'Holiday time-sharing typically alternates between parents on annual basis',
        'Virtual visitation (video calls) is increasingly recognized for long-distance situations',
        'Grandparent visitation rights are protected under specific circumstances'
      ]
    },
    parentingPlans: {
      required: true,
      elements: [
        'Detailed time-sharing schedule including regular rotation, weekends, holidays, and school breaks',
        'Specific holiday and vacation schedules with clear alternating arrangements between parents',
        'Transportation arrangements and responsibility for travel costs between parents\' residences',
        'Communication methods between parents and guidelines for parent-child contact during time-sharing',
        'Decision-making authority allocation for education, healthcare, religious, and extracurricular activities',
        'Dispute resolution procedures including mediation requirements before court intervention',
        'Relocation provisions and notification requirements for moves affecting time-sharing schedules',
        'Any restrictions, conditions, or special accommodations for either parent',
        'Child care arrangements during work hours and right of first refusal provisions',
        'Guidelines for introducing new romantic partners to the children'
      ],
      tips: [
        'California courts favor detailed, specific parenting plans that minimize future disputes',
        'Include provisions for California\'s diverse geography and potential travel distances',
        'Address how to handle missed time-sharing due to traffic, work, or emergencies',
        'Include specific language about meeting locations and travel considerations',
        'Specify how major decisions will be made and communicated between parents',
        'Address the child\'s participation in extracurricular activities and school events',
        'Include technology use guidelines and virtual time-sharing provisions',
        'Plan for extended summer arrangements and school break schedules'
      ]
    },
    childSupport: {
      guidelines: 'California uses a complex formula based on both parents\' gross income, time spent with children, and other factors outlined in Family Code Section 4055',
      factors: [
        'Gross monthly income of both parents from all sources',
        'Number of children covered by the support order',
        'Percentage of time each parent has with the children',
        'Cost of health insurance premiums for the children',
        'Childcare expenses necessary for employment or education',
        'Extraordinary medical expenses not covered by insurance',
        'Other court-ordered support obligations',
        'Hardship deductions for either parent',
        'Special needs of the children requiring additional expenses'
      ],
      calculation: 'California Child Support Guidelines using a complex mathematical formula that considers both parents\' income and time-sharing',
      modifications: 'Significant change in circumstances required, typically 20% change in support amount or significant change in income or time-sharing',
      enforcement: 'Strong enforcement through California Department of Child Support Services including wage garnishment, asset seizure, tax intercept, and license suspension',
      detailedInfo: {
        basicCalculation: {
          description: 'California uses a complex mathematical formula that considers both parents\' net disposable income and the percentage of time each parent has with the children.',
          steps: [
            'Calculate each parent\'s gross monthly income from all verifiable sources',
            'Determine allowable deductions including taxes, mandatory retirement, and existing support orders',
            'Calculate net disposable monthly income for each parent after allowable deductions',
            'Determine the percentage of time each parent has with the children',
            'Apply the California guideline formula: CS = K[HN - (H%)(TN)]',
            'Add costs for health insurance, childcare, and extraordinary expenses',
            'Apply any hardship deductions or special circumstances adjustments',
            'Calculate final monthly support amount payable by higher-earning parent'
          ]
        },
        incomeTypes: {
          included: [
            'Wages, salary, commissions, bonuses, and overtime pay',
            'Self-employment income (gross receipts minus ordinary business expenses)',
            'Investment income, dividends, interest, and capital gains',
            'Rental property income (after reasonable expenses)',
            'Retirement benefits, pensions, and annuity payments',
            'Social Security benefits (including disability and survivor benefits)',
            'Unemployment compensation and workers\' compensation benefits',
            'Military pay including base pay, allowances, and special pay',
            'Spousal support or maintenance received from previous relationships',
            'Trust income and regularly received gifts or inheritances',
            'Business income from partnerships, corporations, or LLCs'
          ],
          excluded: [
            'CalWORKs, food stamps, and other need-based public assistance',
            'Foster care payments received for non-biological children',
            'Child support received for other children not subject to current order',
            'Supplemental Security Income (SSI)',
            'Income of new spouse or cohabitating partner',
            'One-time gifts or inheritances (unless creating ongoing income)',
            'Student loans, grants, and educational financial aid',
            'Reimbursement for job-related expenses'
          ]
        },
        deductions: {
          mandatory: [
            'Federal income taxes based on appropriate filing status',
            'State income taxes (California state taxes)',
            'State disability insurance (SDI)',
            'Social Security and Medicare taxes (FICA)',
            'Mandatory retirement contributions required by employer',
            'Union dues required as condition of employment',
            'Court-ordered child support for other children',
            'Court-ordered spousal support to former spouse'
          ],
          hardship: [
            'Extraordinary health expenses for parent or parent\'s other children',
            'Uninsured catastrophic losses',
            'Minimum basic living expenses of parent\'s other children',
            'Expenses for parent\'s other children for whom support is not otherwise ordered'
          ]
        },
        timeSharingAdjustment: {
          description: 'California\'s formula automatically accounts for the percentage of time each parent has with the children, which can significantly affect support amounts.',
          calculation: 'The formula uses actual time-sharing percentages to determine support, with equal time-sharing potentially resulting in no support or offset calculations',
          factors: [
            'Actual overnight time-sharing documented over previous 12 months',
            'Both parents\' income levels and the disparity between them',
            'Additional costs during time-sharing periods',
            'Reduced expenses for the other parent during time-sharing'
          ]
        },
        additionalExpenses: {
          healthInsurance: {
            description: 'Cost of health insurance premiums specifically for covering the children',
            allocation: 'Added to basic support amount and allocated proportionally between parents',
            requirements: 'Must be reasonable in cost, accessible to children, and provide adequate coverage'
          },
          childcare: {
            description: 'Necessary childcare costs directly related to employment, job training, or education',
            allocation: 'Added to basic support amount and allocated proportionally between parents',
            requirements: 'Must be work-related or education-related necessity'
          },
          extraordinaryMedical: {
            description: 'Uninsured medical, dental, and vision expenses',
            examples: [
              'Dental treatment not covered by insurance',
              'Orthodontic treatment and appliances',
              'Vision care including glasses and contact lenses',
              'Mental health counseling and therapy',
              'Physical therapy and rehabilitation',
              'Prescription medications not covered by insurance',
              'Medical equipment and supplies',
              'Treatment for chronic conditions'
            ],
            allocation: 'Shared proportionally between parents based on their respective income percentages'
          }
        }
      }
    },
    resources: {
      courts: [
        {
          name: 'California Courts Self-Help Center',
          url: 'https://www.courts.ca.gov/selfhelp.htm',
          description: 'Official California court system self-help resources with forms, procedures, and guidance',
          phone: '(916) 263-1000'
        },
        {
          name: 'Los Angeles County Family Court',
          url: 'https://www.lacourt.org/division/familylaw/FL0001.aspx',
          description: 'Family court serving Los Angeles County',
          address: '111 North Hill Street, Los Angeles, CA 90012',
          phone: '(213) 830-0803'
        },
        {
          name: 'San Francisco County Family Court',
          url: 'https://www.sfsuperiorcourt.org/divisions/family',
          description: 'Family court serving San Francisco County',
          address: '400 McAllister Street, San Francisco, CA 94102',
          phone: '(415) 551-4000'
        },
        {
          name: 'Orange County Family Court',
          url: 'https://www.occourts.org/self-help/family/',
          description: 'Family court serving Orange County',
          address: '341 The City Drive South, Orange, CA 92868',
          phone: '(657) 622-8397'
        }
      ],
      support: [
        {
          name: 'California Department of Child Support Services',
          url: 'https://childsupport.ca.gov/',
          phone: '1-866-901-3212',
          description: 'Official state agency for child support establishment, modification, and enforcement'
        },
        {
          name: 'Legal Aid Association of California',
          url: 'https://laac.org/',
          phone: '1-800-551-5554',
          description: 'Free legal services for low-income families in family law matters statewide'
        },
        {
          name: 'California State Bar Family Law Section',
          url: 'https://www.calbar.ca.gov/Public/Need-Legal-Help/Using-a-Lawyer/Lawyer-Referral-Service',
          description: 'Professional resources and attorney referrals for family law'
        },
        {
          name: 'California Partnership to End Domestic Violence',
          url: 'https://www.cpedv.org/',
          phone: '1-800-524-4765',
          description: 'Statewide domestic violence resources and legal advocacy'
        }
      ],
      legal: [
        {
          name: 'California State Bar Lawyer Referral Service',
          url: 'https://www.calbar.ca.gov/Public/Need-Legal-Help/Lawyer-Referral-Service',
          phone: '1-866-442-2529',
          description: 'Professional attorney referrals for family law cases throughout California'
        },
        {
          name: 'UCLA School of Law Legal Clinic',
          url: 'https://law.ucla.edu/academics/clinical-program/',
          description: 'Student-supervised legal services for qualifying family law matters'
        },
        {
          name: 'California Collaborative Practice Council',
          url: 'https://www.cacollaborative.org/',
          description: 'Network of collaborative divorce attorneys and professionals in California'
        }
      ],
      educational: [
        {
          name: 'Children First Program',
          url: 'https://www.courts.ca.gov/programs-childrenfirst.htm',
          description: 'Parent education program for divorcing parents with minor children (required in many counties)'
        },
        {
          name: 'California Parenting Plan Resources',
          url: 'https://www.courts.ca.gov/selfhelp-custody.htm',
          description: 'Comprehensive guide to creating effective parenting plans in California'
        }
      ]
    },
    bestInterestsFactors: [
      'The health, safety, and welfare of the child',
      'Any history of abuse by one parent against the child or against the other parent',
      'The nature and amount of contact with both parents',
      'The habitual or continual illegal use of controlled substances or habitual or continual abuse of alcohol',
      'The capacity of each parent to provide for the needs of the child',
      'The wishes of the child, if the child is of sufficient age and capacity',
      'Any other factor the court determines to be relevant'
    ],
    practicalTips: [
      'California courts strongly favor detailed, specific parenting plans that serve the child\'s best interests',
      'Document all interactions and communications with the other parent using email or text when possible',
      'Complete any required parent education programs as early as possible in your case',
      'Be prepared for mandatory mediation - California courts require it in most contested custody cases',
      'Keep detailed records of your time with your children and any expenses related to their care',
      'Follow all court orders precisely - violations can seriously impact future custody decisions',
      'Consider hiring a local attorney familiar with California family court procedures and judges',
      'Take advantage of California\'s extensive self-help resources and family law facilitators'
    ]
  }
};

export default californiaData;